import React, { useState } from "react";
import i18next from "src/i18n/config";
import { DangerButton, PrimaryButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { showErrorToast } from "src/ui/shared/toasts/Toasts";
import { getErrorMessage } from "src/ui/shared/toasts/useQueryErrorToast";

interface Props {
    title?: string;
    open?: boolean;
    onConfirmDisabled?: boolean;
    dangerButton?: boolean;
    primaryActionText?: string;
    secondaryActionText?: string;
    onConfirm: () => Promise<void> | void;
    onClose: () => void;
}

export function ConfirmDialog({
    title = i18next.t("modals:confirmation.title"),
    open = false,
    dangerButton = false,
    onConfirmDisabled,
    primaryActionText = i18next.t("common:button.confirm"),
    secondaryActionText = i18next.t("common:button.cancel"),
    onConfirm,
    onClose,
    children,
}: React.PropsWithChildren<Props>): React.JSX.Element {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        try {
            setLoading(true);
            await onConfirm();
            onClose();
        } catch (error) {
            showErrorToast(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" && !onConfirmDisabled && !loading) {
            await handleConfirm();
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            onKeyDown={onKeyDown}
            title={title}
            secondaryAction={
                <SecondaryButton onClick={handleClose} disabled={loading}>
                    {secondaryActionText}
                </SecondaryButton>
            }
            endButton={
                dangerButton ? (
                    <DangerButton onClick={handleConfirm} disabled={onConfirmDisabled || loading} loading={loading}>
                        {primaryActionText}
                    </DangerButton>
                ) : (
                    <PrimaryButton onClick={handleConfirm} disabled={onConfirmDisabled || loading} loading={loading}>
                        {primaryActionText}
                    </PrimaryButton>
                )
            }
        >
            {children}
        </Modal>
    );
}
